// adminfrontend/src/admin_ui/pages/AdminShipmentsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import adminInventoryApi from "../services/adminInventoryApi";
import adminLogisticsApi from "../services/adminLogisticsApi";
import "../styles/Admin.css";

function fmtDate(ts) {
  if (!ts) return "-";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toISOString().slice(0, 10);
  } catch {
    return String(ts);
  }
}

function pickList(data, keys) {
  if (!data || data.ok !== true) return [];
  for (const k of keys) {
    const v = data[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

function AdminShipmentsPage({ adminEmail, onLogout }) {
  // basics
  const [branches, setBranches] = useState([]);
  const [originLocations, setOriginLocations] = useState([]);
  const [destLocations, setDestLocations] = useState([]);

  // create shipment form
  const [originBranchId, setOriginBranchId] = useState("");
  const [destinationBranchId, setDestinationBranchId] = useState("");
  const [originLocationId, setOriginLocationId] = useState("");
  const [destinationLocationId, setDestinationLocationId] = useState("");
  const [transitLocationId, setTransitLocationId] = useState(""); // optional
  const [carrierName, setCarrierName] = useState("");
  const [trackingRef, setTrackingRef] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ productId: "", batchId: "", quantity: 1 }]);

  // list filters
  const [status, setStatus] = useState("");
  const [filterOriginBranchId, setFilterOriginBranchId] = useState("");
  const [filterDestinationBranchId, setFilterDestinationBranchId] = useState("");
  const [shipmentNo, setShipmentNo] = useState("");

  // list data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [limit] = useState(100);
  const [offset] = useState(0);

  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const normalizedRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  function setItem(idx, patch) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function addItem() {
    setItems((prev) => [...prev, { productId: "", batchId: "", quantity: 1 }]);
  }
  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  async function loadBranches() {
    const bData = await adminInventoryApi.listBranches().catch(() => null);
    const bList = pickList(bData, ["branches", "items", "rows"]);
    setBranches(bList);

    if (!originBranchId && bList[0]) setOriginBranchId(String(bList[0].id));
    if (!destinationBranchId && bList[0]) setDestinationBranchId(String(bList[0].id));
    if (!filterOriginBranchId && bList[0]) setFilterOriginBranchId("");
    if (!filterDestinationBranchId && bList[0]) setFilterDestinationBranchId("");
  }

  function findTransitLocationId(locations) {
    // migration seeds Transit location per branch: name='Transit', type='transit'
    const t =
      locations.find((l) => String(l.type || "").toLowerCase() === "transit") ||
      locations.find((l) => String(l.name || "").toLowerCase() === "transit") ||
      null;
    return t ? String(t.id) : "";
  }

  async function loadOriginLocations(branchId) {
    if (!branchId) return;
    // reset to avoid stale location ids from another branch
    setOriginLocationId("");
    const lData = await adminInventoryApi.listLocations(Number(branchId)).catch(() => null);
    const list = pickList(lData, ["locations", "items", "rows"]);
    setOriginLocations(list);

    if (list[0]) setOriginLocationId(String(list[0].id));

    // auto-pick transit if available
    const transitId = findTransitLocationId(list);
    setTransitLocationId(transitId || "");
  }

  async function loadDestLocations(branchId) {
    if (!branchId) return;
    // reset to avoid stale location ids from another branch
    setDestinationLocationId("");
    const lData = await adminInventoryApi.listLocations(Number(branchId)).catch(() => null);
    const list = pickList(lData, ["locations", "items", "rows"]);
    setDestLocations(list);

    if (list[0]) setDestinationLocationId(String(list[0].id));
  }

  async function loadShipments() {
    setLoading(true);
    setError("");
    try {
      const data = await adminLogisticsApi.listShipments({
        limit,
        offset,
        status: status || undefined,
        originBranchId: filterOriginBranchId ? Number(filterOriginBranchId) : undefined,
        destinationBranchId: filterDestinationBranchId ? Number(filterDestinationBranchId) : undefined,
        shipmentNo: shipmentNo.trim() || undefined,
      });

      if (!data || data.ok !== true) throw new Error("shipments_failed");

      const list = data.shipments || data.items || data.rows || [];
      setRows(Array.isArray(list) ? list : []);
      setTotal(Number(data.total || list.length || 0));
    } catch (e) {
      console.error("[AdminShipmentsPage] load error:", e);
      setError("Unable to load shipments. Check login or server.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        await loadBranches();
      } catch (e) {
        console.error("[AdminShipmentsPage] init error:", e);
        setError("Unable to load branches.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      await loadOriginLocations(originBranchId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originBranchId]);

  useEffect(() => {
    (async () => {
      await loadDestLocations(destinationBranchId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationBranchId]);

  useEffect(() => {
    // initial list load with empty filters (matches your admin behavior preference)
    (async () => {
      await loadShipments();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    setMutating(true);
    setError("");
    setOkMsg("");

    try {
      const cleanedItems = items
        .map((it) => {
          const pid = Number(String(it.productId).replace(/[^\d]/g, ""));
          const bid = it.batchId ? Number(String(it.batchId).replace(/[^\d]/g, "")) : null;
          const qty = Number(it.quantity);

          return {
            productId: Number.isFinite(pid) && pid > 0 ? pid : null,
            batchId: Number.isFinite(bid) && bid > 0 ? bid : null,
            quantity: Number.isFinite(qty) && qty > 0 ? qty : null,
          };
        })
        .filter((it) => it.productId && it.quantity);

      const payload = {
        originBranchId: Number(originBranchId),
        destinationBranchId: Number(destinationBranchId),
        originLocationId: Number(originLocationId),
        destinationLocationId: Number(destinationLocationId),
        transitLocationId: transitLocationId ? Number(transitLocationId) : null,
        carrierName: carrierName.trim() || null,
        trackingRef: trackingRef.trim() || null,
        notes: notes.trim() || null,
        items: cleanedItems,
      };

      const data = await adminLogisticsApi.createShipment(payload);
      if (!data || data.ok !== true) throw new Error("create_shipment_failed");

      setOkMsg("Shipment created.");
      setCarrierName("");
      setTrackingRef("");
      setNotes("");
      setItems([{ productId: "", batchId: "", quantity: 1 }]);

      await loadShipments();
    } catch (e) {
      console.error("[AdminShipmentsPage] create error:", e);
      const msg =
        e?.response?.data?.error === "bad_request"
          ? "Unable to create shipment. Check required fields and item lines."
          : "Unable to create shipment. Check required fields and item lines.";
      setError(msg);
    } finally {
      setMutating(false);
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Shipments</h1>
            <p className="ucx-admin-subtitle">Create and track branch-to-branch transfers (Logistics).</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={loadShipments} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        {error ? <div className="ucx-admin-status ucx-admin-status-error">{error}</div> : null}
        {okMsg ? <div className="ucx-admin-status ucx-admin-status-success">{okMsg}</div> : null}

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Create Shipment</div>

          <form className="ucx-admin-form" onSubmit={onCreate}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Origin branch *</label>
                <select value={originBranchId} onChange={(e) => setOriginBranchId(e.target.value)}>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} {b.code ? `(${b.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Origin location *</label>
                <select value={originLocationId} onChange={(e) => setOriginLocationId(e.target.value)}>
                  {originLocations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} {l.code ? `(${l.code})` : ""} {l.type ? `- ${l.type}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Transit location (optional)</label>
                <select value={transitLocationId} onChange={(e) => setTransitLocationId(e.target.value)}>
                  <option value="">(none)</option>
                  {originLocations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} {l.code ? `(${l.code})` : ""} {l.type ? `- ${l.type}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Destination branch *</label>
                <select value={destinationBranchId} onChange={(e) => setDestinationBranchId(e.target.value)}>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} {b.code ? `(${b.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Destination location *</label>
                <select value={destinationLocationId} onChange={(e) => setDestinationLocationId(e.target.value)}>
                  {destLocations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} {l.code ? `(${l.code})` : ""} {l.type ? `- ${l.type}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Carrier name</label>
                <input value={carrierName} onChange={(e) => setCarrierName(e.target.value)} placeholder="Optional" />
              </div>
            </div>

            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Tracking ref</label>
                <input value={trackingRef} onChange={(e) => setTrackingRef(e.target.value)} placeholder="Optional" />
              </div>

              <div className="ucx-admin-field" style={{ gridColumn: "span 2" }}>
                <label>Notes</label>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
              </div>
            </div>

            <div className="ucx-admin-lineitems">
              <div className="ucx-admin-lineitems-head">
                <div className="ucx-admin-panel-title" style={{ marginBottom: 0 }}>
                  Items
                </div>
                <button type="button" className="ucx-btn ucx-btn-outline" onClick={addItem}>
                  + Add item
                </button>
              </div>

              <div className="ucx-admin-table-wrapper">
                <table className="ucx-admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: 160 }}>Product ID *</th>
                      <th style={{ width: 160 }}>Batch ID</th>
                      <th style={{ width: 140 }}>Qty *</th>
                      <th style={{ width: 120 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            className="ucx-admin-input-compact"
                            value={it.productId}
                            onChange={(e) => setItem(idx, { productId: e.target.value })}
                            placeholder="e.g. 12"
                          />
                        </td>
                        <td>
                          <input
                            className="ucx-admin-input-compact"
                            value={it.batchId}
                            onChange={(e) => setItem(idx, { batchId: e.target.value })}
                            placeholder="Optional"
                          />
                        </td>
                        <td>
                          <input
                            className="ucx-admin-input-compact"
                            type="number"
                            min="1"
                            value={it.quantity}
                            onChange={(e) => setItem(idx, { quantity: e.target.value })}
                          />
                        </td>
                        <td>
                          <div className="ucx-admin-actions">
                            <button
                              type="button"
                              className="ucx-btn ucx-btn-outline"
                              onClick={() => removeItem(idx)}
                              disabled={items.length <= 1}
                              title={items.length <= 1 ? "At least one item required" : "Remove"}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ucx-admin-form-actions" style={{ marginTop: 12 }}>
                <button type="submit" className="ucx-btn ucx-btn-primary" disabled={mutating}>
                  {mutating ? "Creating…" : "Create shipment"}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-toolbar">
            <div>
              <div className="ucx-admin-panel-title" style={{ marginBottom: 2 }}>
                Shipments List
              </div>
              <div className="ucx-admin-toolbar-hint">
                Showing {normalizedRows.length} / {total}
              </div>
            </div>

            <div className="ucx-admin-filters ucx-admin-filters-inventory">
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                <option value="draft">draft</option>
                <option value="ready">ready</option>
                <option value="in_transit">in_transit</option>
                <option value="delivered">delivered</option>
                <option value="cancelled">cancelled</option>
              </select>

              <select value={filterOriginBranchId} onChange={(e) => setFilterOriginBranchId(e.target.value)}>
                <option value="">All origin branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>

              <select value={filterDestinationBranchId} onChange={(e) => setFilterDestinationBranchId(e.target.value)}>
                <option value="">All destination branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>

              <input value={shipmentNo} onChange={(e) => setShipmentNo(e.target.value)} placeholder="Shipment no (SH-000001)" />

              <button type="button" className="ucx-btn ucx-btn-outline" onClick={loadShipments} disabled={loading}>
                Apply
              </button>

              <button
                type="button"
                className="ucx-btn ucx-btn-outline ucx-admin-clear-filters"
                onClick={() => {
                  setStatus("");
                  setFilterOriginBranchId("");
                  setFilterDestinationBranchId("");
                  setShipmentNo("");
                }}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>Shipment No</th>
                  <th>Status</th>
                  <th>Origin</th>
                  <th>Destination</th>
                  <th>Carrier</th>
                  <th>Tracking</th>
                  <th>Created</th>
                  <th style={{ width: 110 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="ucx-admin-empty">
                      Loading…
                    </td>
                  </tr>
                ) : normalizedRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="ucx-admin-empty">
                      No shipments found.
                    </td>
                  </tr>
                ) : (
                  normalizedRows.map((s) => (
                    <tr key={s.id}>
                      <td>{s.shipment_no || s.shipmentNo || "-"}</td>
                      <td>{s.status || "-"}</td>
                      <td>{s.origin_branch_name || s.originBranchName || s.origin_branch_id || s.originBranchId || "-"}</td>
                      <td>
                        {s.destination_branch_name || s.destinationBranchName || s.destination_branch_id || s.destinationBranchId || "-"}
                      </td>
                      <td>{s.carrier_name || s.carrierName || "-"}</td>
                      <td>{s.tracking_ref || s.trackingRef || "-"}</td>
                      <td>{fmtDate(s.created_at || s.createdAt)}</td>
                      <td>
                        <Link className="ucx-btn ucx-btn-outline" to={`/logistics/shipments/${s.id}`}>
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AdminShipmentsPage;
